/**
 * RTWO Native Currency Engine — Native Currency Registry Service
 */

import type { CurrencyId, RTWONativeCurrency } from '../domain-models';
import type {
  INativeCurrencyRegistryService,
  NativeCurrencyRegistryResult,
} from '../service-interfaces';
import type { NATVRepositories } from '../repositories/natv-repositories';
import type { AssetAdapterRegistry, ProtocolAdapterRegistry } from '../registry/adapter-registries';
import { NATVInternalEventTypes } from '../events/natv-event-bus';
import { BaseNATVService, type NATVServiceContext } from './base-natv-service';

const ARCHITECTURE_CURRENCY: RTWONativeCurrency = {
  currencyId: 'rtwo-native-001',
  symbol: 'RTWO',
  name: 'RTWO Native Currency',
  decimals: null,
  currencyType: 'native_protocol_currency',
  status: 'waiting_for_genesis',
  createdAt: null,
};

export class NativeCurrencyRegistryService
  extends BaseNATVService
  implements INativeCurrencyRegistryService
{
  readonly serviceId = 'natv-currency-registry-001';
  readonly serviceName = 'Native Currency Registry Service';
  readonly description =
    'Native currency registry — placeholder, requires Genesis Configuration. RTWO currency NOT claimed as live.';
  readonly integrations = ['Core Engine', 'Blockchain Protocol Engine', 'System Registry'];
  readonly requiresGenesisConfiguration = true;
  readonly requiresLedgerConnection = false;

  constructor(
    context: NATVServiceContext,
    private readonly repositories: NATVRepositories,
    private readonly protocolRegistry: ProtocolAdapterRegistry,
    private readonly assetRegistry: AssetAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_genesis_configuration';
  }

  async registerCurrency(
    symbol: string,
    name: string,
    decimals: number
  ): Promise<NativeCurrencyRegistryResult> {
    if (!this.protocolRegistry.isConfigured()) {
      return {
        success: false,
        currencyId: null,
        error:
          'Currency registration requires Genesis Configuration — Genesis Configuration Not Created',
        isPlaceholder: true,
        source: 'Native Currency Engine — Genesis Configuration Not Created',
      };
    }

    const adapterResult = await this.protocolRegistry
      .getActiveAdapter()
      .registerCurrency(symbol, name, decimals);

    if (adapterResult?.currency) {
      this.repositories.currencies.save(adapterResult.currency);
      if (this.assetRegistry.isConfigured()) {
        await this.assetRegistry
          .getActiveAdapter()
          .linkNativeCurrency(adapterResult.currency.currencyId);
      }
      this.context.eventBus.emit(NATVInternalEventTypes.CURRENCY_REGISTERED, {
        currencyId: adapterResult.currency.currencyId,
      });
    }

    return {
      success: false,
      currencyId: null,
      error:
        'Currency registration requires Genesis Configuration — Genesis Configuration Not Created',
      isPlaceholder: true,
      source: 'Native Currency Engine — Genesis Configuration Not Created',
    };
  }

  getCurrency(_currencyId: CurrencyId): RTWONativeCurrency | null {
    return ARCHITECTURE_CURRENCY;
  }

  getAllCurrencies(): RTWONativeCurrency[] {
    return [ARCHITECTURE_CURRENCY];
  }

  getCurrencyCount(): number {
    return 1;
  }
}
